import type { PaymentEvidence } from "@lafayette/api-client";
import { stateWord } from "@lafayette/domain";
import type { ReactNode } from "react";
import { DotPill } from "../../requests/components/parts";
import { Badge } from "../../ui/badge";
import { Icon } from "../../ui/icon";
import { Notice } from "../../ui/notice";
import { StaffOnlyNote } from "../../ui/staff-only-note";
import { lookOf } from "../../ui/status-look";
import type { PaymentFailure } from "../errors";
import { evidenceMeanings, statusLook } from "../labels";

// The small pieces every payment screen shares. A badge always carries a word, never a colour alone. They are built from the kit and
// from the shared parts of the request screens (`requests/components/parts.tsx`).

const evidenceLook = {
  FAKE: { tone: "warning", icon: "alert-02" },
  REAL: { tone: "info", icon: "target-02" },
  NONE: { tone: "neutral", icon: "circle" },
} as const;

/**
 * FAKE, REAL or NONE on every screen that shows a payment. FAKE is the fake gateway: no money moved, and the result is never launch
 * evidence. The word is the label; the title and the hidden text say what it means.
 */
export function EvidenceLabel({ evidence }: Readonly<{ evidence: PaymentEvidence }>) {
  const meaning = evidenceMeanings[evidence];
  const { tone, icon } = evidenceLook[evidence];
  return (
    <Badge
      tone={tone}
      title={meaning}
      data-evidence={evidence}
      className="h-6 py-0 font-bold tracking-wider"
    >
      <Icon name={icon} />
      {evidence}
      <span className="sr-only">. {meaning}</span>
    </Badge>
  );
}

export function PaymentStatusBadge({ status }: Readonly<{ status: string | null }>) {
  const word = stateWord("payment", "staffShort", status);
  return <DotPill tone={lookOf(statusLook, status).tone}>{word}</DotPill>;
}

/** A refusal or an error in words. An error is announced at once. */
export function PaymentFailureNotice({
  failure,
  hint,
}: Readonly<{ failure: PaymentFailure; hint?: ReactNode }>) {
  return (
    <Notice tone={failure.kind === "conflict" ? "warning" : "danger"} role="alert">
      <p>
        {failure.message}
        {hint === undefined || hint === null ? null : <> {hint}</>}
      </p>
    </Notice>
  );
}

/** The marker of a block that only staff read. It is text, so a screen reader reads it too. */
export function StaffOnly({ children }: Readonly<{ children?: ReactNode }>) {
  return <StaffOnlyNote>{children}</StaffOnlyNote>;
}
